import { MutationFunction } from "@apollo/client";
import deleteIcon from "@assets/images/delete_btn.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { CSSProperties } from "@material-ui/styles";
import {
  AltMatSortField,
  editdAlternatedWithoutIDUrl
} from "@saleor/alternatedMaterial/urls";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import {
  AlterMaterialFilterInput,
  AlternativeMaterialOsInput,
  AlternativeMaterialsOsQuery,
  DeleteAlternativeMaterialMutation,
  DeleteAlternativeMaterialMutationVariables
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { maybe, renderCollection } from "@saleor/misc";
import { Exact } from "@saleor/sdk/dist/apollo/types";
import { ListProps, ListSettings, RelayToFlat, SortPage } from "@saleor/types";
import { formatDateTime } from "@saleor/utils/date";
import { getArrowDirection } from "@saleor/utils/sort";
import editIcon from "assets/images/edit_btn.svg";
import React, { useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";

import MyTypography from "../../../components/MyTypography";
import StickyHorizontalScrollBar from "../../../components/StickyHorizontalScrollBar";
import StickyVerticalScrollBar from "../../../components/StickyVerticalScrollBar";
import TableCellHeader from "../../../components/TableCellHeader";
import DialogError from "../Common/DialogError";
import ModalConfirmDeleteItem from "../ModalConfirmDeleteItem";
const freezeCell: CSSProperties = {
  position: "sticky",
  left: 0,
  minWidth: "54px",
  padding: 0,
  borderRadius: "0px",
  background: "inherit",
  paddingLeft: "20px !important",
  paddingRight: "0px !important"
};
const containerStyle: CSSProperties = {
  height: "400px"
};

const useStyles = makeStyles(
  theme => ({
    titleTable: {
      marginTop: "40px"
    },
    root: {
      marginTop: "24px",
      overflowX: "auto",
      width: "100%",
      border: "1px solid #009FB8",
      borderRadius: "4px",
      "& .MuiTableCell-body": {
        color: "#222222"
      },

      "& .AltMatTable-root-363 .MuiTableCell-head *": {
        color: "#222222"
      },
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "&.first-child:not(.MuiTableCell-paddingCheckbox)": {
        paddingLeft: "20px",
        paddingRight: "0px"
      }
    },
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      minWidth: "200px",
      paddingLeft: 12,
      "& .MuiTypography-root": {
        color: "#222222",
        fontWeight: "400"
      }
    },
    col400: {
      minWidth: 400,
      color: theme.palette.grey[900]
    },
    col150: {
      minWidth: 150,
      color: theme.palette.grey[900]
    },
    col160: {
      minWidth: 160,
      color: theme.palette.grey[900]
    },
    col180: {
      minWidth: 180,
      color: theme.palette.grey[900]
    },
    col100: {
      minWidth: 100,
      color: theme.palette.grey[900]
    },

    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      padding: "8px 0px"
    },
    rowClickable: {
      cursor: "pointer"
    },
    colActive: {
      ...freezeCell,
      left: "53px",
      boxShadow: "1px 0px 1px #ccc",
      paddingRight: "0px",
      paddingLeft: "0px !important",
      minWidth: 48,
      "& .MuiFormControlLabel-root": {
        margin: 0
      }
    },
    colFreeze: {
      ...freezeCell
    },
    statusIcon: {
      width: 8,
      height: 8,
      marginRight: 8,
      borderRadius: "50%"
    },
    title: {
      marginTop: 40,
      marginBottom: 24
    },
    positionTop: {
      zIndex: 6
    },
    headActionTxt: {
      fontSize: "12px",
      fontWeight: 400
    },
    flex: { display: "flex", alignItems: "center" }
  }),
  { name: "AltMatTable" }
);

interface AlternatedMaterialTableProps extends ListProps, SortPage<any> {
  data: RelayToFlat<AlternativeMaterialsOsQuery["alternativeMaterialsOs"]>;
  settings: ListSettings;
  onPreviousFirstPage: () => void;
  setVisible: (item: boolean) => void;
  onNextLastPage: () => void;
  onDeleteItem: MutationFunction<
    DeleteAlternativeMaterialMutation,
    DeleteAlternativeMaterialMutationVariables
  >;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  refetch?: (
    variables?: Partial<
      Exact<{
        filter?: AlterMaterialFilterInput;
        sortBy?: AlternativeMaterialOsInput;
        before?: string;
        after?: string;
        first?: number;
        last?: number;
      }>
    >
  ) => void;
}

const AlternatedMaterialTable: React.FC<AlternatedMaterialTableProps> = ({
  data,
  settings,
  onSort,
  sort,
  pageInfo,
  disabled,
  onPreviousPage,
  onNextPage,
  onNextLastPage,
  onPreviousFirstPage,
  onUpdateListSettings,
  latestPageItemNumber,
  totalCount,
  onDeleteItem,
  refetch,
  setVisible
}) => {
  const history = useHistory();
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpenDeleteItem, setIsOpenDeleteItem] = useState<boolean>(false);
  const [currentItemId, setCurrentItemId] = useState<string>("");
  const [visibles, setVisibles] = useState<boolean>(false);
  const [firstLines, setFirstLines] = useState<string | null>(null);
  const handleCloseDeleteItem = () => {
    setIsOpenDeleteItem(prev => !prev);
  };
  const handleOnCloseError = () => {
    setVisibles(false);
  };
  const handleConfirmDeleteItem = async () => {
    const data = await onDeleteItem({
      variables: { alternativeMaterialOsId: currentItemId }
    });
    setIsOpenDeleteItem(false);
    const errors = data?.data?.deleteAlternativeMaterialOs?.errors;
    if (
      refetch &&
      data.data.deleteAlternativeMaterialOs?.status &&
      !errors[0]?.message
    ) {
      await refetch();
      handleOnComplete();
    } else {
      setVisibles(true);
      const materialOwnError = data.data.deleteAlternativeMaterialOs.errors[0];
      const messages = materialOwnError.message.split("\n");

      const newFirstLines = messages.length > 0 ? messages[0] : "";
      setFirstLines(newFirstLines);
    }
  };

  const handleOpenDeleteItem = (alternativeMaterialOsId: string) => {
    setCurrentItemId(alternativeMaterialOsId);
    setIsOpenDeleteItem(prev => !prev);
  };

  const handleOnComplete = () => {
    setVisible(true);
  };
  return (
    <>
      <div className={classes.root} ref={containerRef} style={containerStyle}>
        <Table stickyHeader data-testid="alternatedMaterial-table">
          <TableHead>
            <TableRow>
              <TableCellHeader
                className={`${classes.colFreeze} ${classes.positionTop}`}
              >
                <span className={classes.headActionTxt}> Delete</span>
              </TableCellHeader>
              <TableCellHeader
                className={`${classes.colActive} ${classes.positionTop}`}
              >
                <span className={classes.headActionTxt}>/Edit</span>
              </TableCellHeader>
              <TableCellHeader
                className={classes.colPiNumber}
                direction={
                  sort.sort === AltMatSortField.SALE_ORG
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.SALE_ORG)}
              >
                {intl.formatMessage({
                  defaultMessage: "Sale Org."
                })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col400}
                direction={
                  sort.sort === AltMatSortField.SOLD_TO
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.SOLD_TO)}
              >
                {intl.formatMessage({ defaultMessage: "Sold-to Party" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col180}
                direction={
                  sort.sort === AltMatSortField.MATERIAL_INPUT
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.MATERIAL_INPUT)}
              >
                {intl.formatMessage({ defaultMessage: "Material Input" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col160}
                direction={
                  sort.sort === AltMatSortField.ALTERNATED_MATERIAL
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.ALTERNATED_MATERIAL)}
              >
                {intl.formatMessage({ defaultMessage: "Alternated Material" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col100}
                direction={
                  sort.sort === AltMatSortField.DIA
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.DIA)}
              >
                {intl.formatMessage({ defaultMessage: "Dia" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col100}
                direction={
                  sort.sort === AltMatSortField.TYPE
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.TYPE)}
              >
                {intl.formatMessage({ defaultMessage: "Type" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col100}
                direction={
                  sort.sort === AltMatSortField.PRIORITY
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.PRIORITY)}
              >
                {intl.formatMessage({ defaultMessage: "Priority" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col160}
                direction={
                  sort.sort === AltMatSortField.LAST_UPDATED_DATE
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.LAST_UPDATED_DATE)}
              >
                {intl.formatMessage({ defaultMessage: "Last Updated Date" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col150}
                direction={
                  sort.sort === AltMatSortField.LAST_UPDATED_BY
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(AltMatSortField.LAST_UPDATED_BY)}
              >
                {intl.formatMessage({ defaultMessage: "Last Updated By" })}
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(data, (item, index) => (
              <TableRow className={classes.rowClickable} key={index}>
                <TableCell
                  className={classes.colFreeze}
                  onClick={() => {
                    handleOpenDeleteItem(item.id || "0");
                  }}
                  id="deleteButton"
                  data-testid="deleteButton"
                >
                  <SVG src={deleteIcon} />
                </TableCell>
                <TableCell
                  className={classes.colActive}
                  onClick={() => {
                    history.push(
                      `${editdAlternatedWithoutIDUrl}${item.alternativeMaterial.id}`
                    );
                  }}
                  id="editButton"
                  data-testid="editButton"
                >
                  <SVG src={editIcon} />
                </TableCell>
                <TableCell className={classes.colPiNumber}>
                  <MyTypography variant="subtitle3">
                    {maybe(
                      () =>
                        `${item.alternativeMaterial.salesOrganization.code} -  
                     ${item.alternativeMaterial.salesOrganization.name}`
                    )}
                  </MyTypography>
                </TableCell>
                <TableCell>
                  {maybe(
                    () =>
                      `${item.alternativeMaterial.soldTo.code} -  
                   ${item.alternativeMaterial.soldTo.name}`
                  )}
                </TableCell>
                <TableCell>
                  {maybe(() => item.alternativeMaterial.materialOwn.code)}
                </TableCell>
                <TableCell>{maybe(() => item.materialOs.code)}</TableCell>
                <TableCell>
                  {maybe(() => (item.diameter === null ? "-" : item.diameter))}
                </TableCell>
                <TableCell>
                  {maybe(() =>
                    item.alternativeMaterial.type === null
                      ? "-"
                      : item.alternativeMaterial.type
                  )}
                </TableCell>
                <TableCell>{maybe(() => item.priority)}</TableCell>
                <TableCell>
                  {maybe(() =>
                    formatDateTime(item.alternativeMaterial.updatedAt)
                  )}
                </TableCell>
                <TableCell>
                  {maybe(() => item.alternativeMaterial.updatedBy.firstName)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StickyHorizontalScrollBar container={containerRef} />
        <StickyVerticalScrollBar container={containerRef} />
      </div>
      <Box className={classes.paginateFooter} data-testid="pagination-wrapper">
        <TablePaginationCustom
          disabled={disabled}
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          isBackFistPage
          isNextLastPage
          isShowIndex
        />
      </Box>

      <ModalConfirmDeleteItem
        onClose={handleCloseDeleteItem}
        open={isOpenDeleteItem}
        submitData={handleConfirmDeleteItem}
      />
      <DialogError
        visible={visibles}
        onClose={handleOnCloseError}
        title={"ไม่สามารถทำรายการได้"}
        firstLine={firstLines}
        secondLine={null}
      />
    </>
  );
};

export default AlternatedMaterialTable;
